import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [newSupplier, setNewSupplier] = useState({ name: '', contact_info: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('خطأ في جلب البيانات');
    }
  };

  const handleAdd = async () => {
    if (!newSupplier.name.trim()) {
      toast.error('الرجاء إدخال اسم المورد');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/suppliers`, newSupplier);
      toast.success('تم إضافة المورد بنجاح');
      setNewSupplier({ name: '', contact_info: '' });
      fetchSuppliers();
    } catch (error) {
      toast.error('خطأ في إضافة المورد');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success('تم حذف المورد بنجاح');
      fetchSuppliers();
    } catch (error) {
      toast.error('خطأ في حذف المورد');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-slate-100 p-6">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Manrope'}}>
              إدارة الموردين
            </h1>
            <p className="text-slate-600">أضف وأدر بيانات الموردين</p>
          </div>
          <Button 
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/procurement')} 
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="w-5 h-5 shrink-0" />
            <span>العودة</span>
          </Button>
        </div>

        <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90">
          <CardHeader>
            <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>إضافة مورد جديد</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="supplier-name">اسم المورد</Label>
                <Input
                  id="supplier-name"
                  data-testid="supplier-name-input"
                  placeholder="اسم المورد"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="supplier-contact">معلومات الاتصال (اختياري)</Label>
                <Input
                  id="supplier-contact"
                  data-testid="supplier-contact-input"
                  placeholder="رقم الهاتف أو البريد الإلكتروني"
                  value={newSupplier.contact_info}
                  onChange={(e) => setNewSupplier({...newSupplier, contact_info: e.target.value})}
                  className="mt-2"
                />
              </div>
              <Button 
                data-testid="add-supplier-btn"
                onClick={handleAdd} 
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:shadow-lg"
              >
                <Plus className="w-4 h-4 ml-2" />
                إضافة المورد
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/90">
          <CardHeader>
            <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>قائمة الموردين</CardTitle>
          </CardHeader>
          <CardContent>
            {suppliers.length === 0 ? (
              <p className="text-center text-slate-500 py-8">لا يوجد موردين. قم بإضافة مورد جديد</p>
            ) : (
              <div className="space-y-3">
                {suppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    data-testid={`supplier-item-${supplier.id}`}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{supplier.name}</p>
                      {supplier.contact_info && (
                        <p className="text-sm text-slate-600 mt-1">{supplier.contact_info}</p>
                      )}
                    </div>
                    <Button
                      data-testid={`delete-supplier-${supplier.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(supplier.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
